import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FlagIcon from "@mui/icons-material/Flag";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import MehrsanOperationsApi from "../../../../infrastructure/api/mehrsanOperations";
import { toJalaliFullDate } from "../../../middleware/middleware";
import MehrsanLogAttachment from "./MehrsanLogAttachment";
import {
  MEHRSAN_FOLLOW_UP_STATUSES,
  MEHRSAN_PROCESS_TYPE_OTHER,
  MEHRSAN_PROCESS_TYPE_POWER_PURCHASE,
  MEHRSAN_PROCESS_TYPES,
  MEHRSAN_STATUS_COMPLETED,
  MEHRSAN_STATUS_FINAL_STAGE,
  getStatusChipColor,
  isCaseWorkspaceOperationsApi,
  resolveOperationsSource,
} from "./mehrsanOperationsConstants";
import ProcessOperationOwnerField from "./ProcessOperationOwnerField";
import {
  buildInitialLogForm,
  buildMehrsanStatusUpdateActionTitle,
  getAttachmentUrl,
  getAuthorName,
  getEntityId,
  getStatusLabel,
  hasMehrsanCompletion,
  hasMehrsanFinalStage,
  isMehrsanProcessClosed,
  normalizeTimelineLogs,
  parseApiErrorMessage,
} from "./mehrsanOperationsUtils";

const normalizeList = (res) => {
  const data = res?.data ?? res?.Data;
  return Array.isArray(data) ? data : [];
};

const ProcessCardSkeleton = () => (
  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
    <Skeleton variant="text" width="60%" height={22} />
    <Skeleton variant="text" width="40%" sx={{ mt: 0.5 }} />
    <Skeleton variant="rounded" width={72} height={28} sx={{ mt: 1 }} />
  </Paper>
);

const MehrsanOperationsTab = ({
  requestInfoId,
  detailApi,
  fileBaseUrl = "",
  active = true,
  onStageChange,
}) => {
  const operationsSource = resolveOperationsSource(detailApi);
  const canEditAssignee = isCaseWorkspaceOperationsApi(detailApi);
  const lastLoadedRequestRef = useRef(null);

  const [view, setView] = useState("list");
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [processes, setProcesses] = useState([]);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [submittingLog, setSubmittingLog] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneeSaving, setAssigneeSaving] = useState(false);

  const [logForm, setLogForm] = useState(buildInitialLogForm(null));

  const [createForm, setCreateForm] = useState({
    processType: MEHRSAN_PROCESS_TYPE_POWER_PURCHASE,
    processTypeOtherTitle: "",
    title: "",
    referenceNumber: "",
    description: "",
  });

  const hasPowerPurchase = useMemo(
    () =>
      processes.some(
        (p) =>
          Number(p.processType ?? p.ProcessType) ===
          MEHRSAN_PROCESS_TYPE_POWER_PURCHASE
      ),
    [processes]
  );

  const powerPurchaseProcess = useMemo(
    () =>
      processes.find(
        (p) =>
          Number(p.processType ?? p.ProcessType) ===
          MEHRSAN_PROCESS_TYPE_POWER_PURCHASE
      ) ?? null,
    [processes]
  );

  const createSubmitDisabled =
    createLoading ||
    (Number(createForm.processType) === MEHRSAN_PROCESS_TYPE_POWER_PURCHASE &&
      hasPowerPurchase);

  const loadProcesses = useCallback(
    async (force = false) => {
      if (!requestInfoId) return;
      const key = String(requestInfoId);
      if (!force && lastLoadedRequestRef.current === key) {
        return;
      }

      setLoading(true);
      setListError(null);
      try {
        const res = await MehrsanOperationsApi.getByRequestId(
          operationsSource,
          requestInfoId
        );
        if (res?.isSuccess === false) {
          const msg = parseApiErrorMessage(
            res,
            "بارگذاری فرآیندهای مهرسان ناموفق بود."
          );
          setListError(msg);
          setProcesses([]);
          return;
        }
        setProcesses(normalizeList(res));
        lastLoadedRequestRef.current = key;
      } catch {
        setListError("بارگذاری فرآیندهای مهرسان ناموفق بود.");
        setProcesses([]);
      } finally {
        setLoading(false);
      }
    },
    [operationsSource, requestInfoId]
  );

  const loadProcessDetail = useCallback(
    async (processId) => {
      if (!processId) return;
      setDetailLoading(true);
      try {
        const res = await MehrsanOperationsApi.getById(
          operationsSource,
          processId
        );
        const data = res?.data ?? res?.Data;
        if (res?.isSuccess === false || !data) {
          toast.error(
            parseApiErrorMessage(res, "بارگذاری جزئیات فرآیند ناموفق بود.")
          );
          return;
        }
        setSelectedProcess(data);
        setView("detail");
        setLogForm(buildInitialLogForm(data));
      } catch {
        toast.error("بارگذاری جزئیات فرآیند ناموفق بود.");
      } finally {
        setDetailLoading(false);
      }
    },
    [operationsSource]
  );

  useEffect(() => {
    if (!active || !requestInfoId) return;
    const key = String(requestInfoId);
    if (lastLoadedRequestRef.current !== key) {
      setView("list");
      setSelectedProcess(null);
      setListError(null);
    }
    loadProcesses();
  }, [active, requestInfoId, loadProcesses]);

  useEffect(() => {
    if (!active || !canEditAssignee) return;
    const loadExperts = async () => {
      try {
        const res = await MehrsanOperationsApi.getAssigneeOptions(operationsSource);
        if (res?.isSuccess) setAssigneeOptions(res.data || []);
      } catch {
        setAssigneeOptions([]);
      }
    };
    loadExperts();
  }, [active, operationsSource, canEditAssignee]);

  const saveProcessAssignee = useCallback(
    async (userId) => {
      const processId = getEntityId(selectedProcess);
      if (!processId) return;
      setAssigneeSaving(true);
      try {
        const res = await MehrsanOperationsApi.update(operationsSource, {
          id: processId,
          updateAssignedToUser: true,
          assignedToUserId: userId,
        });
        if (res?.isSuccess) {
          toast.success("مسئول فرآیند به‌روزرسانی شد");
          await loadProcessDetail(processId);
          await loadProcesses(true);
        } else {
          toast.error(
            res?.message || "کاربر انتخاب‌شده برای مسئول فرآیند معتبر نیست."
          );
        }
      } catch (err) {
        toast.error(
          parseApiErrorMessage(err, "کاربر انتخاب‌شده برای مسئول فرآیند معتبر نیست.")
        );
      } finally {
        setAssigneeSaving(false);
      }
    },
    [selectedProcess, operationsSource, loadProcessDetail, loadProcesses]
  );

  const timelineLogs = useMemo(
    () =>
      normalizeTimelineLogs(
        selectedProcess?.logs || selectedProcess?.Logs || []
      ),
    [selectedProcess]
  );

  const processClosed = isMehrsanProcessClosed(selectedProcess);
  const mehrsanFinalStage = hasMehrsanFinalStage(selectedProcess, timelineLogs);
  const mehrsanCompleted = hasMehrsanCompletion(selectedProcess, timelineLogs);

  const powerPurchaseClosed = isMehrsanProcessClosed(powerPurchaseProcess);
  const powerPurchaseFinalStage = hasMehrsanFinalStage(powerPurchaseProcess, []);
  const powerPurchaseCompleted = hasMehrsanCompletion(powerPurchaseProcess, []);

  useEffect(() => {
    if (!active || loading || createOpen || view !== "list" || !processes.length) return;

    const target =
      powerPurchaseProcess ?? (processes.length === 1 ? processes[0] : null);
    if (!target) return;

    const id = getEntityId(target);
    if (id) loadProcessDetail(id);
  }, [
    active,
    loading,
    createOpen,
    view,
    processes,
    powerPurchaseProcess,
    loadProcessDetail,
  ]);

  const submitLog = async (
    payload,
    successMessage = "وضعیت مهرسان بروزرسانی شد.",
    processOverride = null
  ) => {
    const process = processOverride ?? selectedProcess;
    const processId = getEntityId(process);
    const closed = isMehrsanProcessClosed(process);
    if (!processId || submittingLog || closed) return false;

    setSubmittingLog(true);
    try {
      const res = await MehrsanOperationsApi.addLog(operationsSource, {
        MehrsanProcessId: processId,
        ...payload,
      });
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "ثبت وضعیت ناموفق بود."));
        return false;
      }
      toast.success(successMessage);
      if (!processOverride || getEntityId(selectedProcess) === processId) {
        setLogForm(buildInitialLogForm(process));
      }
      await loadProcesses(true);
      onStageChange?.();
      if (getEntityId(selectedProcess) === processId) {
        await loadProcessDetail(processId);
      }
      return true;
    } catch {
      toast.error("ثبت وضعیت ناموفق بود. اتصال یا دسترسی را بررسی کنید.");
      return false;
    } finally {
      setSubmittingLog(false);
    }
  };

  const handleStatusUpdate = async () => {
    const status = Number(logForm.status);
    await submitLog({
      Status: status,
      ActionTitle: buildMehrsanStatusUpdateActionTitle(status),
      Description: logForm.description?.trim() || null,
      ReferenceNumber: logForm.referenceNumber?.trim() || null,
    });
  };

  const handleQuickFinalStage = async (processOverride = null) => {
    const process = processOverride ?? selectedProcess;
    const alreadyFinal = processOverride
      ? hasMehrsanFinalStage(process, [])
      : mehrsanFinalStage;
    if (alreadyFinal) {
      toast.info("مرحله پایانی قبلاً ثبت شده است.");
      return;
    }
    await submitLog(
      {
        Status: MEHRSAN_STATUS_FINAL_STAGE,
        ActionTitle: "ثبت مرحله پایانی",
        Description: logForm.description?.trim() || null,
      },
      "مرحله پایانی ثبت شد.",
      processOverride
    );
  };

  const handleQuickCompletion = async (processOverride = null) => {
    const process = processOverride ?? selectedProcess;
    const finalDone = processOverride
      ? hasMehrsanFinalStage(process, [])
      : mehrsanFinalStage;
    const completed = processOverride
      ? hasMehrsanCompletion(process, [])
      : mehrsanCompleted;
    if (!finalDone) {
      toast.warning("ابتدا باید مرحله پایانی ثبت شود.");
      return;
    }
    if (completed) {
      toast.info("پایان مهرسان قبلاً ثبت شده است.");
      return;
    }
    await submitLog(
      {
        Status: MEHRSAN_STATUS_COMPLETED,
        ActionTitle: "پایان مهرسان",
        Description: logForm.description?.trim() || null,
      },
      "پایان مهرسان ثبت شد و فرآیند بسته شد.",
      processOverride
    );
  };

  const renderFinalStagePanel = ({
    finalStageDone,
    completionDone,
    closed,
    onFinalStage,
    onCompletion,
  }) => {
    if (closed) {
      return (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          فرآیند مهرسان بسته شده است.
          {finalStageDone && completionDone
            ? " مرحله پایانی و پایان مهرسان ثبت شده‌اند."
            : null}
        </Alert>
      );
    }

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          mb: 1.5,
          borderRadius: 1.5,
          borderColor: "warning.main",
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          اتمام مهرسان
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<FlagIcon />}
            disabled={submittingLog || finalStageDone}
            onClick={onFinalStage}
            fullWidth
          >
            {finalStageDone ? "مرحله پایانی ثبت شده" : "ثبت مرحله پایانی"}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleOutlineIcon />}
            disabled={submittingLog || !finalStageDone || completionDone}
            onClick={onCompletion}
            fullWidth
          >
            {completionDone ? "پایان مهرسان ثبت شده" : "پایان مهرسان"}
          </Button>
        </Stack>
        {!finalStageDone ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            پس از ثبت مرحله پایانی، دکمه «پایان مهرسان» فعال می‌شود.
          </Typography>
        ) : null}
      </Paper>
    );
  };

  const handleCreateProcess = async () => {
    if (!requestInfoId || createLoading) return;
    const type = Number(createForm.processType);
    if (type === MEHRSAN_PROCESS_TYPE_OTHER && !createForm.processTypeOtherTitle?.trim()) {
      toast.warning("برای نوع «سایر» عنوان دستی الزامی است.");
      return;
    }
    if (type === MEHRSAN_PROCESS_TYPE_POWER_PURCHASE && hasPowerPurchase) {
      toast.warning("فرآیند قرارداد خرید برق برای این پرونده قبلاً ثبت شده است.");
      return;
    }

    setCreateLoading(true);
    try {
      const res = await MehrsanOperationsApi.create(operationsSource, {
        RequestInfoId: Number(requestInfoId),
        ProcessType: type,
        ProcessTypeOtherTitle:
          type === MEHRSAN_PROCESS_TYPE_OTHER
            ? createForm.processTypeOtherTitle.trim()
            : null,
        Title: createForm.title?.trim() || null,
        ReferenceNumber: createForm.referenceNumber?.trim() || null,
        Description: createForm.description?.trim() || null,
      });
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "ثبت فرآیند ناموفق بود."));
        return;
      }
      toast.success("فرآیند مهرسان ثبت شد.");
      setCreateOpen(false);
      setCreateForm({
        processType: MEHRSAN_PROCESS_TYPE_POWER_PURCHASE,
        processTypeOtherTitle: "",
        title: "",
        referenceNumber: "",
        description: "",
      });
      await loadProcesses(true);
    } catch {
      toast.error("ثبت فرآیند ناموفق بود.");
    } finally {
      setCreateLoading(false);
    }
  };

  const renderTimelineItem = (log) => {
    const logId = getEntityId(log);
    const status = log.status ?? log.Status;
    const statusLabel = getStatusLabel(status, log.statusTitle ?? log.StatusTitle);

    return (
      <Paper
        key={logId ?? `${log.actionTitle}-${log.createOn}`}
        variant="outlined"
        sx={{ p: 1.25, borderRadius: 1.5 }}
      >
        <Chip
          size="small"
          label={statusLabel}
          color={getStatusChipColor(status)}
          variant="outlined"
          sx={{ mb: 0.75 }}
        />
        <Typography variant="subtitle2" fontWeight={600}>
          {log.actionTitle || log.ActionTitle || "—"}
        </Typography>
        <Stack spacing={0.35} sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" component="div">
            <AccessTimeIcon sx={{ fontSize: 13, verticalAlign: "middle", ml: 0.5 }} />
            {toJalaliFullDate(log.createOn ?? log.CreateOn) || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            <AssignmentIndIcon sx={{ fontSize: 13, verticalAlign: "middle", ml: 0.5 }} />
            {getAuthorName(log.createdByFullName ?? log.CreatedByFullName)}
          </Typography>
          {log.nextFollowUpDate || log.NextFollowUpDate ? (
            <Typography variant="caption" color="warning.main" component="div">
              پیگیری بعدی:{" "}
              {toJalaliFullDate(log.nextFollowUpDate ?? log.NextFollowUpDate)}
            </Typography>
          ) : null}
          {log.referenceNumber || log.ReferenceNumber ? (
            <Typography variant="caption" color="text.secondary" component="div">
              مرجع: {log.referenceNumber ?? log.ReferenceNumber}
            </Typography>
          ) : null}
          {log.description || log.Description ? (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.8rem", lineHeight: 1.5 }}
              >
                {log.description ?? log.Description}
              </Typography>
            </>
          ) : null}
          <MehrsanLogAttachment
            url={getAttachmentUrl(log)}
            fileBaseUrl={fileBaseUrl}
            compact
          />
        </Stack>
      </Paper>
    );
  };

  const renderProcessCard = (item) => {
    const itemId = getEntityId(item);
    const typeTitle = item.processTypeTitle || item.ProcessTypeTitle || "—";
    const status = item.currentStatus ?? item.CurrentStatus;
    const statusTitle = getStatusLabel(
      status,
      item.currentStatusTitle || item.CurrentStatusTitle
    );

    return (
      <Paper
        key={itemId}
        variant="outlined"
        sx={{ p: 1.25, borderRadius: 1.5 }}
      >
        <Stack spacing={0.75}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, lineHeight: 1.35 }}>
              {item.title || item.Title || typeTitle}
            </Typography>
            <Chip
              size="small"
              label={statusTitle}
              color={getStatusChipColor(status)}
              variant="outlined"
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            نوع: {typeTitle}
          </Typography>
          {item.referenceNumber || item.ReferenceNumber ? (
            <Typography variant="caption" color="text.secondary">
              مرجع: {item.referenceNumber ?? item.ReferenceNumber}
            </Typography>
          ) : null}
          {item.lastActionTitle || item.LastActionTitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
              آخرین اقدام: {item.lastActionTitle ?? item.LastActionTitle}
            </Typography>
          ) : null}
          {item.lastActionAt || item.LastActionAt ? (
            <Typography variant="caption" color="text.secondary">
              آخرین بروزرسانی:{" "}
              {toJalaliFullDate(item.lastActionAt ?? item.LastActionAt) || "—"}
            </Typography>
          ) : null}
          {item.assignedToFullName || item.AssignedToFullName ? (
            <Typography variant="caption" color="text.secondary">
              مسئول: {item.assignedToFullName ?? item.AssignedToFullName}
            </Typography>
          ) : null}
          <Button
            size="small"
            variant="outlined"
            onClick={() => loadProcessDetail(itemId)}
            sx={{ alignSelf: "flex-start", mt: 0.25 }}
          >
            جزئیات
          </Button>
        </Stack>
      </Paper>
    );
  };

  if (loading && !processes.length && !listError) {
    return (
      <Stack spacing={1}>
        <ProcessCardSkeleton />
        <ProcessCardSkeleton />
      </Stack>
    );
  }

  if (listError && !processes.length) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => loadProcesses(true)}>
            تلاش مجدد
          </Button>
        }
      >
        {listError}
      </Alert>
    );
  }

  if (view === "detail") {
    const typeTitle =
      selectedProcess?.processTypeTitle || selectedProcess?.ProcessTypeTitle;
    const statusTitle = getStatusLabel(
      selectedProcess?.currentStatus ?? selectedProcess?.CurrentStatus,
      selectedProcess?.currentStatusTitle || selectedProcess?.CurrentStatusTitle
    );

    return (
      <Box sx={{ width: "100%" }}>
        {processes.length > 1 ? (
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setView("list");
              setSelectedProcess(null);
            }}
            sx={{ mb: 1 }}
          >
            بازگشت به لیست
          </Button>
        ) : null}

        {detailLoading && !selectedProcess ? (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={72} />
            <Skeleton variant="rounded" height={100} />
          </Stack>
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, borderRadius: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {selectedProcess?.title || selectedProcess?.Title || typeTitle}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75, gap: 0.5 }}>
                <Chip size="small" label={typeTitle || "—"} variant="outlined" />
                <Chip
                  size="small"
                  label={statusTitle}
                  color={getStatusChipColor(
                    selectedProcess?.currentStatus ?? selectedProcess?.CurrentStatus
                  )}
                />
              </Stack>
              {selectedProcess?.referenceNumber || selectedProcess?.ReferenceNumber ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  مرجع:{" "}
                  {selectedProcess.referenceNumber ?? selectedProcess.ReferenceNumber}
                </Typography>
              ) : null}
            </Paper>

            {renderFinalStagePanel({
              finalStageDone: mehrsanFinalStage,
              completionDone: mehrsanCompleted,
              closed: processClosed,
              onFinalStage: () => handleQuickFinalStage(),
              onCompletion: () => handleQuickCompletion(),
            })}

            <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, borderRadius: 1.5 }}>
              <ProcessOperationOwnerField
                label="مسئول فرآیند"
                experts={assigneeOptions}
                assignedToUserId={
                  selectedProcess?.assignedToUserId ??
                  selectedProcess?.AssignedToUserId ??
                  ""
                }
                assignedToFullName={
                  selectedProcess?.assignedToFullName ??
                  selectedProcess?.AssignedToFullName
                }
                assignedAt={
                  selectedProcess?.assignedAt ?? selectedProcess?.AssignedAt
                }
                canEdit={canEditAssignee}
                saving={assigneeSaving}
                onAssigneeChange={saveProcessAssignee}
              />
            </Paper>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                تاریخچه وضعیت
              </Typography>
              {detailLoading ? <CircularProgress size={16} /> : null}
            </Stack>

            {!timelineLogs.length ? (
              <Alert severity="info" sx={{ mt: 0.5 }}>
                هنوز بروزرسانی وضعیتی ثبت نشده است.
              </Alert>
            ) : (
              <Stack spacing={1.25}>{timelineLogs.map(renderTimelineItem)}</Stack>
            )}

            {!processClosed ? (
              <Paper variant="outlined" sx={{ p: 1.25, mt: 1.5, borderRadius: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  بروزرسانی وضعیت
                </Typography>
                <Stack spacing={1.5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>وضعیت</InputLabel>
                    <Select
                      label="وضعیت"
                      value={logForm.status}
                      disabled={submittingLog}
                      onChange={(e) =>
                        setLogForm((prev) => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                    >
                      {MEHRSAN_FOLLOW_UP_STATUSES.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="توضیحات (اختیاری)"
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={submittingLog}
                    value={logForm.description}
                    onChange={(e) =>
                      setLogForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                  <TextField
                    size="small"
                    label="شماره پیگیری / مرجع"
                    fullWidth
                    disabled={submittingLog}
                    value={logForm.referenceNumber}
                    onChange={(e) =>
                      setLogForm((prev) => ({
                        ...prev,
                        referenceNumber: e.target.value,
                      }))
                    }
                  />
                  <Button
                    variant="outlined"
                    onClick={handleStatusUpdate}
                    disabled={submittingLog}
                  >
                    {submittingLog ? "در حال ثبت..." : "ثبت بروزرسانی وضعیت"}
                  </Button>
                </Stack>
              </Paper>
            ) : null}
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          فرآیندهای مهرسان ({processes.length})
        </Typography>
        <Stack direction="row" spacing={0.5} justifyContent={{ xs: "stretch", sm: "flex-end" }}>
          <Button
            size="small"
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={() => loadProcesses(true)}
            disabled={loading}
          >
            بروزرسانی
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            افزودن فرآیند
          </Button>
        </Stack>
      </Stack>

      {listError ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {listError}
        </Alert>
      ) : null}

      {powerPurchaseProcess
        ? renderFinalStagePanel({
            finalStageDone: powerPurchaseFinalStage,
            completionDone: powerPurchaseCompleted,
            closed: powerPurchaseClosed,
            onFinalStage: () => handleQuickFinalStage(powerPurchaseProcess),
            onCompletion: () => handleQuickCompletion(powerPurchaseProcess),
          })
        : null}

      {loading && processes.length > 0 ? (
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={20} />
        </Box>
      ) : null}

      {!processes.length && !loading ? (
        <Alert severity="info">
          هنوز فرآیند مهرسانی برای این پرونده ثبت نشده است.
        </Alert>
      ) : (
        <Stack spacing={1}>{processes.map(renderProcessCard)}</Stack>
      )}

      <Dialog
        open={createOpen}
        onClose={() => !createLoading && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>افزودن فرآیند مهرسان</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            {hasPowerPurchase ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                قرارداد خرید برق برای این پرونده از قبل وجود دارد.
              </Alert>
            ) : null}
            <FormControl size="small" fullWidth disabled={createLoading}>
              <InputLabel>نوع فرآیند</InputLabel>
              <Select
                label="نوع فرآیند"
                value={createForm.processType}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    processType: e.target.value,
                  }))
                }
              >
                {MEHRSAN_PROCESS_TYPES.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    disabled={
                      opt.value === MEHRSAN_PROCESS_TYPE_POWER_PURCHASE &&
                      hasPowerPurchase
                    }
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {Number(createForm.processType) === MEHRSAN_PROCESS_TYPE_OTHER ? (
              <TextField
                size="small"
                label="عنوان نوع (الزامی)"
                required
                fullWidth
                disabled={createLoading}
                value={createForm.processTypeOtherTitle}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    processTypeOtherTitle: e.target.value,
                  }))
                }
              />
            ) : null}
            <TextField
              size="small"
              label="عنوان"
              fullWidth
              disabled={createLoading}
              value={createForm.title}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, title: e.target.value }))
              }
            />
            <TextField
              size="small"
              label="شماره پیگیری / مرجع"
              fullWidth
              disabled={createLoading}
              value={createForm.referenceNumber}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  referenceNumber: e.target.value,
                }))
              }
            />
            <TextField
              size="small"
              label="توضیحات"
              fullWidth
              multiline
              minRows={2}
              disabled={createLoading}
              value={createForm.description}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={createLoading}>
            انصراف
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateProcess}
            disabled={createSubmitDisabled}
          >
            {createLoading ? "در حال ثبت..." : "ثبت"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

MehrsanOperationsTab.propTypes = {
  requestInfoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  detailApi: PropTypes.string,
  fileBaseUrl: PropTypes.string,
  active: PropTypes.bool,
  onStageChange: PropTypes.func,
};

export default MehrsanOperationsTab;
